import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { Box, Button, Typography } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import { GEMINI_GRADIENT_SOFT } from '../../theme';

export const VERIFICATION_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))',
  },
  gap: 2,
  width: '100%',
} as const;

export function VerificationDetailField({
  label,
  children,
  fullWidth,
}: {
  label: string;
  children: ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <Box sx={{ minWidth: 0, ...(fullWidth ? { gridColumn: { xs: '1 / -1', lg: 'span 3' } } : {}) }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.4 }}>{children}</Box>
    </Box>
  );
}

export function monoValueSx(theme: Theme) {
  return {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: '0.8125rem',
    fontWeight: 600,
    wordBreak: 'break-all' as const,
    color: theme.palette.text.primary,
  };
}

export function identitySessionStatusColor(
  status: string,
): 'default' | 'success' | 'warning' | 'error' {
  const s = status.toUpperCase();
  if (s === 'SUCCESS' || s === 'COMPLETED' || s === 'IDV_COMPLETED') return 'success';
  if (s === 'REJECTED' || s === 'REJECTED_DUPLICATE' || s === 'FAILED') return 'error';
  if (s === 'PENDING' || s === 'IN_PROGRESS' || s === 'PROCESSING') return 'warning';
  return 'default';
}

export function verificationSubsectionPaperSx(theme: Theme) {
  return {
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    overflow: 'hidden',
    boxShadow: theme.shadows[1],
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    bgcolor: 'background.paper',
  };
}

export function verificationSubsectionHeaderSx(theme: Theme) {
  return {
    px: 2,
    py: 1.25,
    flexShrink: 0,
    borderBottom: '1px solid',
    borderColor: 'divider',
    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 72%)`,
  };
}

export function verificationAccordionSx(theme: Theme) {
  return {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '12px !important',
    overflow: 'hidden',
    boxShadow: theme.shadows[1],
    '&:before': { display: 'none' },
    // disableGutters zeros margin when expanded — keep gap below the panel.
    mb: 2,
    '&.Mui-expanded': { mb: 2 },
  };
}

export function verificationAccordionSummarySx(theme: Theme) {
  return {
    px: 2,
    minHeight: 52,
    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 85%)`,
    '& .MuiAccordionSummary-content': { my: 1 },
  };
}

export function RecordDetailGrid({ record, emptyLabel = 'No values recorded.' }: { record: Record<string, unknown>; emptyLabel?: string }) {
  const theme = useTheme();
  const entries = Object.entries(record).filter(([, v]) => v != null && typeof v !== 'object');
  if (entries.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 1.25,
        width: '100%',
      }}
    >
      {entries.map(([key, value]) => (
        <Box
          key={key}
          sx={{
            p: 1.1,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            minWidth: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
            {key}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {String(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function WorkspaceFooter({ to, label }: { to: string; label: string }) {
  return (
    <Button component={RouterLink} to={to} variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
      {label}
    </Button>
  );
}

export function VerificationTabIntro({
  title,
  description,
}: {
  title: string;
  description: ReactNode;
}) {
  return (
    <Box
      sx={(theme) => ({
        px: 2,
        py: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundImage: GEMINI_GRADIENT_SOFT,
        boxShadow: theme.shadows[1],
      })}
    >
      <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 0.35 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  );
}
